import type { SpaceLimit } from '../../../hooks/useSpaceLimits';
import type { Space } from '../../../lib/flotiq-client/types/api-response';

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { twMerge } from 'tailwind-merge';

import { updateSpacePlan } from '../../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import BuyMoreButton from '../../../components/BuyMoreButton/BuyMoreButton';
import Loader from '../../../components/Loader/Loader';
import Tooltip from '../../../components/Tooltip/Tooltip';

import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';
import { useSpaceLimits } from '../../../hooks/useSpaceLimits';
import useToken from '../../../hooks/useToken';

import {
  ArrowRightThinIcon,
  InformationCircleIcon,
  WarningTriangleIcon,
} from '../../../images/shapes';

const Detail = ({
  detail,
  isTooltipCenter,
  testId,
}: {
  detail: SpaceLimit;
  isTooltipCenter: boolean;
  testId?: string;
}) => {
  const { t } = useTranslation();

  if (!detail.percent || detail.percent < 90 || detail.limit === -1)
    return (
      <div>
        <span className="font-bold">{detail.value}</span>
        {detail.value != null && ' / '}
        {detail.limitLabel}
      </div>
    );

  const isReached = detail.percent >= 100;
  const tooltipKey = isReached
    ? 'Spaces.LimitExceeded'
    : 'Spaces.LimitAlmostExceeded';

  return (
    <div>
      <span
        className={`font-bold ${
          isReached ? 'text-red-600 dark:text-red-400' : ''
        }`}
      >
        {detail.value}
      </span>
      {detail.value != null && ' / '}
      {detail.limitLabel}
      <Tooltip
        tooltip={t(tooltipKey, { label: detail.label })}
        tooltipPlacement={isTooltipCenter ? 'topCenter' : 'topLeft'}
        phoneTooltipPlacement={isTooltipCenter ? 'topCenter' : 'topLeft'}
        additionalClasses="w-fit inline ml-2"
        {...getTestProps(testId, 'exceeded', 'testId')}
      >
        <WarningTriangleIcon
          className={`inline-block w-4 mb-px align-text-bottom ${
            isReached ? 'text-red-600 dark:text-red-400' : 'text-orange-400'
          }`}
        />
      </Tooltip>
    </div>
  );
};

interface SpaceLimitsProps {
  /**
   * Space details id
   */
  id?: string;
  /**
   * Space details plan limits details
   */
  planLimits?: Space['planLimits'];
  /**
   * Space details payment due date
   */
  paymentDueDate?: string;
  /**
   * Space details payment status
   */
  paymentStatus?: string;
  /**
   * Should display plan name
   */
  showPlanName?: boolean;
  /**
   * If buy extra button is visible
   */
  isBuyExtraVisible?: boolean;
  /**
   * Test id for page
   */
  testId?: string;
}

const SpaceLimits = ({
  id = '',
  planLimits,
  paymentDueDate,
  paymentStatus,
  showPlanName = true,
  isBuyExtraVisible = true,
  testId = '',
}: SpaceLimitsProps) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const [isSpaceSaving, setIsSpaceSaving] = useState(false);

  const spaceLimitOptions = useMemo(
    () => ({
      pause: !id,
    }),
    [id],
  );

  const { data: resourcesSpaceDetails, errors } = useSpaceLimits(
    planLimits,
    undefined,
    id,
    spaceLimitOptions,
  );

  useApiErrorsToast(errors);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const handleUpgradeSpace = useCallback(async () => {
    setIsSpaceSaving(true);

    try {
      const { body, status, ok } = await updateSpacePlan(jwt, undefined, {
        id: id,
        planId: planLimits?.id,
        period: 'month',
        uri: window.location.toString(),
      });
      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      setIsSpaceSaving(false);

      if (body.status === 'payment_required') {
        window.location.assign(body.url);
      }
    } catch (error) {
      setIsSpaceSaving(false);

      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
        return;
      }

      toast.error(t('Spaces.Form.CouldntUpdate'));
    }
  }, [jwt, id, planLimits?.id, t]);

  return (
    <>
      {showPlanName && (
        <div
          className={twMerge(
            'flex flex-wrap lg:flex-nowrap items-center justify-between mb-3 text-base lg:text-lg',
          )}
        >
          <div className="pr-5">
            {t('Spaces.YourSpacePlan')}
            <span
              className="font-bold ml-1 xs:text-lg lg:text-xl"
              {...getTestProps(testId, 'plan-name')}
            >
              {planLimits?.visible_name}
            </span>
          </div>
          {paymentStatus === 'trial' ? (
            <div className="flex items-end">
              <span>{t('Spaces.FreeTrialEnds')}</span>
              <span className="font-bold xs:text-lg lg:text-xl pl-2 mb-[-2px]">
                {moment(paymentDueDate).format('DD.MM.YYYY')}
              </span>
              <Button
                as={'button'}
                buttonColor={'borderless'}
                onClick={handleUpgradeSpace}
                iconImage={<ArrowRightThinIcon className="w-3 h-3" />}
                iconPosition={'end'}
                additionalClasses={'ml-auto px-2'}
                additionalChildrenClasses={'flex flex-row-reverse'}
                {...getTestProps(testId, `buy-now`)}
              >
                {t('AccountSettings.BuyNow')}
                {isSpaceSaving && (
                  <Loader
                    size="tiny"
                    type="spinner-grid"
                    additionalClasses="h-4 mt-0.5 mr-1"
                  />
                )}
              </Button>
            </div>
          ) : (
            ''
          )}
        </div>
      )}
      {resourcesSpaceDetails.map((detail) => (
        <div
          key={detail.key}
          className={twMerge(
            'py-2 md:py-2 grid gap-1 md:gap-2 text-base items-center grid-cols-1',
            'border-b border-slate-200 dark:border-slate-800 last:border-none',
            isBuyExtraVisible ? ' sm:grid-cols-3' : 'sm:grid-cols-2',
          )}
          {...getTestProps(testId, detail.key)}
        >
          <div>
            {detail.label}
            {detail.tooltip && (
              <Tooltip
                tooltip={detail.tooltip}
                tooltipPlacement="topCenter"
                phoneTooltipPlacement="topCenter"
                additionalClasses="inline-block align-middle px-1 md:px-2"
                additionalTooltipClasses=""
                {...getTestProps(testId, detail.key, 'testId')}
              >
                <InformationCircleIcon className="text-blue min-w-4 w-4" />
              </Tooltip>
            )}
          </div>
          <Detail
            detail={detail}
            isTooltipCenter={isBuyExtraVisible}
            {...getTestProps(testId, detail.key, 'testId')}
          />
          {isBuyExtraVisible && planLimits && planLimits.price > 0 && (
            <BuyMoreButton
              detail={detail}
              spaceId={id}
              planLimits={planLimits}
              additionalClasses="ml-auto"
              testId={testId}
            />
          )}
        </div>
      ))}
    </>
  );
};

export default SpaceLimits;
